import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Trash2, Check, Plus } from "lucide-react";

const STAGES = ["new", "qualified", "quoted", "confirmed", "traveling", "completed"];
const STAGE_LABEL_FR = { new: "Nouveau", qualified: "Qualifié", quoted: "Devis envoyé", confirmed: "Confirmé", traveling: "En voyage", completed: "Terminé" };
const STAGE_LABEL_EN = { new: "New", qualified: "Qualified", quoted: "Quoted", confirmed: "Confirmed", traveling: "Traveling", completed: "Completed" };

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#0A1128]/10 p-6" data-testid={testid}>
      <div className="overline text-[#D4AF37]">{label}</div>
      <div className="font-serif text-4xl text-[#0A1128] mt-3">{value}</div>
    </div>
  );
}

export default function CRM() {
  const { lang } = useI18n();
  const L = lang === "fr" ? STAGE_LABEL_FR : STAGE_LABEL_EN;
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [subs, setSubs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [newTask, setNewTask] = useState({ title: "", assigned_to: "Lucile", priority: "normal" });
  const [newPartner, setNewPartner] = useState({ name: "", category: "chateau", region: "", contact_email: "", commission: 0 });

  const load = async () => {
    try {
      const [s, l, p, t, sub, b] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/leads"),
        api.get("/partners"),
        api.get("/tasks"),
        api.get("/newsletter/subscribers"),
        api.get("/bookings"),
      ]);
      setStats(s.data); setLeads(l.data); setPartners(p.data); setTasks(t.data); setSubs(sub.data); setBookings(b.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const setStage = async (lead_id, status) => {
    await api.patch(`/leads/${lead_id}`, { status });
    toast.success(lang === "fr" ? "Statut mis à jour" : "Status updated");
    load();
  };
  const toggleTask = async (t) => { await api.patch(`/tasks/${t.task_id}`, { done: !t.done }); load(); };
  const addTask = async (e) => {
    e.preventDefault();
    await api.post("/tasks", newTask);
    setNewTask({ title: "", assigned_to: "Lucile", priority: "normal" });
    load();
  };
  const addPartner = async (e) => {
    e.preventDefault();
    await api.post("/partners", { ...newPartner, commission: Number(newPartner.commission) });
    setNewPartner({ name: "", category: "chateau", region: "", contact_email: "", commission: 0 });
    load();
  };
  const delPartner = async (id) => { await api.delete(`/partners/${id}`); load(); };
  const seed = async () => { await api.post("/seed"); toast.success("Seeded"); load(); };

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase transition-colors ${tab === id ? "bg-[#0A1128] text-[#F9F8F6]" : "text-[#0A1128]/70 hover:text-[#0A1128]"}`}
      data-testid={`crm-tab-${id}`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="overline text-[#D4AF37]">CRM</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl text-[#0A1128] mt-2">
            {lang === "fr" ? "Pilotage interne" : "Internal cockpit"}
          </h1>
        </div>
        <button onClick={seed} className="text-xs tracking-[0.2em] uppercase border border-[#D4AF37] text-[#0A1128] px-4 py-2 hover:bg-[#D4AF37] transition-colors" data-testid="crm-seed">
          {lang === "fr" ? "Charger la démo" : "Load demo"}
        </button>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-[#0A1128]/10 mb-8">
        <TabBtn id="dashboard" label="Dashboard" />
        <TabBtn id="leads" label="Leads" />
        <TabBtn id="bookings" label={lang === "fr" ? "Rendez-vous" : "Bookings"} />
        <TabBtn id="partners" label={lang === "fr" ? "Partenaires" : "Partners"} />
        <TabBtn id="tasks" label={lang === "fr" ? "Tâches" : "Tasks"} />
        <TabBtn id="newsletter" label="Newsletter" />
      </div>

      {tab === "dashboard" && stats && (
        <div className="space-y-10">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Stat label={lang === "fr" ? "Leads" : "Leads"} value={stats.total_leads} testid="stat-leads" />
            <Stat label={lang === "fr" ? "Nouveaux" : "New"} value={stats.new_leads} testid="stat-new" />
            <Stat label={lang === "fr" ? "Confirmés" : "Confirmed"} value={stats.confirmed} testid="stat-confirmed" />
            <Stat label="Bookings" value={stats.bookings} testid="stat-bookings" />
            <Stat label={lang === "fr" ? "Devis" : "Quotes"} value={stats.quotes} testid="stat-quotes" />
            <Stat label={lang === "fr" ? "Abonnés" : "Subs"} value={stats.subscribers} testid="stat-subs" />
          </div>
          <div className="bg-white border border-[#0A1128]/10 p-8">
            <div className="overline text-[#D4AF37]">{lang === "fr" ? "Pipeline (EUR)" : "Pipeline (EUR)"}</div>
            <div className="font-serif text-5xl text-[#0A1128] mt-3">€ {stats.pipeline_value_eur.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-[#0A1128]/10 p-8">
            <div className="overline text-[#D4AF37] mb-4">{lang === "fr" ? "Sources" : "Sources"}</div>
            <ul className="space-y-2">
              {stats.sources.map((s) => (
                <li key={s.source} className="flex justify-between text-sm">
                  <span className="text-[#0A1128]/70">{s.source}</span>
                  <span className="text-[#0A1128] font-medium">{s.count}</span>
                </li>
              ))}
              {stats.sources.length === 0 && <li className="text-[#0A1128]/50 text-sm italic">—</li>}
            </ul>
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div className="space-y-6" data-testid="leads-table">
          {leads.length === 0 && <div className="text-[#0A1128]/50 italic">{lang === "fr" ? "Aucun lead pour l'instant." : "No leads yet."}</div>}
          {leads.map((l) => (
            <div key={l.lead_id} className="bg-white border border-[#0A1128]/10 p-6 grid md:grid-cols-4 gap-4 items-center" data-testid={`lead-${l.lead_id}`}>
              <div>
                <div className="font-serif text-xl text-[#0A1128]">{l.name}</div>
                <div className="text-xs text-[#0A1128]/60">{l.email}</div>
              </div>
              <div className="text-sm text-[#0A1128]/80">
                <div>{l.travel_type} · {l.party_size} pax</div>
                <div className="text-xs text-[#0A1128]/50">{l.source}</div>
              </div>
              <div className="text-xs text-[#0A1128]/60 line-clamp-2">{l.message}</div>
              <select
                value={l.status}
                onChange={(e) => setStage(l.lead_id, e.target.value)}
                className="border border-[#0A1128]/20 px-3 py-2 text-sm bg-white"
                data-testid={`lead-status-${l.lead_id}`}
              >
                {STAGES.map((s) => <option key={s} value={s}>{L[s]}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="space-y-4" data-testid="bookings-table">
          {bookings.length === 0 && <div className="text-[#0A1128]/50 italic">—</div>}
          {bookings.map((b) => (
            <div key={b.booking_id} className="bg-white border border-[#0A1128]/10 p-6 grid md:grid-cols-4 gap-4">
              <div>
                <div className="font-serif text-lg text-[#0A1128]">{b.name}</div>
                <div className="text-xs text-[#0A1128]/60">{b.email}</div>
              </div>
              <div className="text-sm">{new Date(b.slot_iso).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}</div>
              <div className="text-sm text-[#0A1128]/70">{b.travel_type} · {b.party_size} pax</div>
              <a href={b.meet_link} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] underline truncate">{b.meet_link}</a>
            </div>
          ))}
        </div>
      )}

      {tab === "partners" && (
        <div className="space-y-8">
          <form onSubmit={addPartner} className="bg-white border border-[#0A1128]/10 p-6 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="partner-form">
            <input required placeholder={lang === "fr" ? "Nom" : "Name"} value={newPartner.name} onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })} className="border border-[#0A1128]/20 px-3 py-2 text-sm" data-testid="partner-name" />
            <select value={newPartner.category} onChange={(e) => setNewPartner({ ...newPartner, category: e.target.value })} className="border border-[#0A1128]/20 px-3 py-2 text-sm">
              {["chateau", "hotel", "champagne", "photographer", "driver", "other"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <input placeholder={lang === "fr" ? "Région" : "Region"} value={newPartner.region} onChange={(e) => setNewPartner({ ...newPartner, region: e.target.value })} className="border border-[#0A1128]/20 px-3 py-2 text-sm" />
            <input placeholder="Email" value={newPartner.contact_email} onChange={(e) => setNewPartner({ ...newPartner, contact_email: e.target.value })} className="border border-[#0A1128]/20 px-3 py-2 text-sm" />
            <input placeholder="Commission %" type="number" value={newPartner.commission} onChange={(e) => setNewPartner({ ...newPartner, commission: e.target.value })} className="border border-[#0A1128]/20 px-3 py-2 text-sm" />
            <button type="submit" className="bg-[#0A1128] text-[#F9F8F6] px-4 py-2 text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-2" data-testid="partner-add"><Plus size={14} />{lang === "fr" ? "Ajouter" : "Add"}</button>
          </form>
          <div className="grid md:grid-cols-2 gap-4">
            {partners.map((p) => (
              <div key={p.partner_id} className="bg-white border border-[#0A1128]/10 p-6 flex justify-between items-start" data-testid={`partner-${p.partner_id}`}>
                <div>
                  <div className="overline text-[#D4AF37]">{p.category} · {p.region}</div>
                  <div className="font-serif text-xl text-[#0A1128] mt-1">{p.name}</div>
                  <div className="text-xs text-[#0A1128]/60 mt-1">{p.contact_email}</div>
                  <div className="text-xs text-[#0A1128]/60">Commission {p.commission}%</div>
                  {p.notes && <div className="text-xs text-[#0A1128]/70 mt-2 italic">{p.notes}</div>}
                </div>
                <button onClick={() => delPartner(p.partner_id)} className="text-[#E2856E] hover:text-[#0A1128]" data-testid={`partner-del-${p.partner_id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "tasks" && (
        <div className="space-y-6">
          <form onSubmit={addTask} className="bg-white border border-[#0A1128]/10 p-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="task-form">
            <input required placeholder={lang === "fr" ? "Titre" : "Title"} value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} className="border border-[#0A1128]/20 px-3 py-2 text-sm md:col-span-2" data-testid="task-title" />
            <select value={newTask.assigned_to} onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })} className="border border-[#0A1128]/20 px-3 py-2 text-sm">
              {["Lucile", "Pascal", "Camille"].map((n) => <option key={n}>{n}</option>)}
            </select>
            <button type="submit" className="bg-[#0A1128] text-[#F9F8F6] px-4 py-2 text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-2" data-testid="task-add"><Plus size={14} />{lang === "fr" ? "Ajouter" : "Add"}</button>
          </form>
          <ul className="space-y-2">
            {tasks.map((t) => (
              <li key={t.task_id} className={`bg-white border border-[#0A1128]/10 p-4 flex items-center gap-4 ${t.done ? "opacity-50" : ""}`} data-testid={`task-${t.task_id}`}>
                <button onClick={() => toggleTask(t)} className={`w-6 h-6 border ${t.done ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A1128]" : "border-[#0A1128]/30"} flex items-center justify-center`}>{t.done && <Check size={14} />}</button>
                <div className="flex-1">
                  <div className={`text-sm ${t.done ? "line-through" : ""}`}>{t.title}</div>
                  <div className="text-xs text-[#0A1128]/50">{t.assigned_to} · {t.priority}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "newsletter" && (
        <div className="bg-white border border-[#0A1128]/10 p-6">
          <div className="overline text-[#D4AF37] mb-4">{lang === "fr" ? "Abonnés" : "Subscribers"} ({subs.length})</div>
          <ul className="divide-y divide-[#0A1128]/10">
            {subs.map((s) => (
              <li key={s.sub_id} className="py-3 flex justify-between text-sm">
                <span>{s.email}</span>
                <span className="text-[#0A1128]/50">{s.segment} · {s.language}</span>
              </li>
            ))}
            {subs.length === 0 && <li className="text-[#0A1128]/50 italic py-3">—</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
